import { all, byId } from './dom.ts';
import './version.ts';
import './tutorials.ts';
import { animateAvatars, avatarCanvas } from './creator.ts';
import { DEFAULT_CONFIG, type AvatarConfig, parseConfig, project } from './modules/avatars/catalog.ts';
import { FICTION, fictionReady } from './docs-fiction.ts';

// Signed in: the same page, in-fiction (docs-fiction.ts). Each section's facts
// give way to the narrator: you, from the universe that created Omniu, with
// your name and face, in the creator's style. The Plain / Narrative switch flips between them.
(async () => {
  const preview = new URLSearchParams(location.search).has('fiction');
  if (!fictionReady() && !preview) return;
  const res = await fetch('/api/me', { credentials: 'same-origin' }).catch(() => null);
  if (!res?.ok) return;
  const me = (await res.json()) as { displayName: string; avatar: unknown; worldId: string; creatorStyle: Pick<AvatarConfig, 'hair' | 'accessory' | 'aura'> | null };
  const name = me.displayName.trim().split(/\s+/)[0] || me.displayName;
  const names = /(s|x|z|ch|sh)$/i.test(name) ? `${name}es` : `${name}s`;
  const say = (t: string) => t.replaceAll('{names}', names).replaceAll('{name}', name).replaceAll('{world}', me.worldId);
  const face: AvatarConfig = me.avatar ? parseConfig(me.avatar) : DEFAULT_CONFIG;
  const narrator: AvatarConfig = me.creatorStyle ? project(face, { ...face, ...me.creatorStyle }) : face;

  for (const s of FICTION) {
    const section = document.getElementById(s.id);
    if (!section) continue;
    const fic = document.createElement('div');
    fic.className = 'fic';
    const head = document.createElement(s.id === 'overview' ? 'h1' : 'h2');
    head.className = 'fic-head';
    head.textContent = say(s.head);
    const line = document.createElement('div');
    line.className = 'fic-line';
    const who = document.createElement('span');
    who.className = 'fic-who';
    who.append(avatarCanvas(narrator));
    const bubble = document.createElement('blockquote');
    bubble.className = 'neon fic-say';
    for (const p of s.body.split(/\n\s*\n/).filter((x) => x.trim())) {
      const el = document.createElement('p');
      el.textContent = say(p.trim());
      bubble.append(el);
    }
    const cite = document.createElement('cite');
    cite.textContent = name;
    bubble.append(cite);
    line.append(who, bubble);
    fic.append(head, line);
    if (!s.head.trim()) head.classList.add('fic-gap');
    if (!s.body.trim()) bubble.classList.add('fic-gap');
    // A planning section keeps its little animated screen beside the words.
    const stage = section.querySelector('.tut-stage');
    if (stage) section.insertBefore(fic, stage); else section.prepend(fic);
    for (const a of all(document, `.toc a[href="#${s.id}"], .menu a[href="#${s.id}"]`)) {
      a.dataset.fact = a.textContent ?? '';
      a.dataset.fiction = say(s.toc) || a.dataset.fact;
    }
  }

  // Plain or Narrative (Eric's words), remembered on this device.
  const flip = document.createElement('div');
  flip.className = 'fic-toggle';
  flip.id = 'fiction-flip';
  flip.setAttribute('role', 'group');
  flip.innerHTML = '<button type="button" data-mode="plain">Plain</button><button type="button" data-mode="narrative">Narrative</button>';
  const set = (on: boolean) => {
    document.documentElement.classList.toggle('fiction', on);
    for (const b of all(flip, 'button')) b.setAttribute('aria-pressed', String((b.dataset.mode === 'narrative') === on));
    for (const a of all(document, '.toc a[data-fiction], .menu a[data-fiction]')) a.textContent = (on ? a.dataset.fiction : a.dataset.fact) ?? '';
    try { localStorage.setItem('omniu-docs-mode', on ? 'narrative' : 'plain'); } catch { /* fine: just not remembered */ }
  };
  flip.addEventListener('click', (e) => {
    const b = (e.target as Element).closest<HTMLElement>('[data-mode]');
    if (b) set(b.dataset.mode === 'narrative');
  });
  byId('burger').before(flip);
  // Eric's note, in plain white and grey: this is the story; Plain has the facts.
  const notice = document.createElement('p');
  notice.className = 'fic-notice';
  notice.innerHTML = 'Note: this site is a fanciful in-world narrative. See <a href="#overview" data-mode="plain">Plain</a> for info.';
  notice.querySelector('a')!.addEventListener('click', (e) => { e.preventDefault(); set(false); scrollTo({ top: 0 }); });
  document.querySelector('.docs-main')?.prepend(notice);
  let saved: string | null = null;
  try { saved = localStorage.getItem('omniu-docs-mode'); } catch { saved = null; }
  set(saved !== 'plain');
  animateAvatars(document.body);
})();

// Docs page: the section menu, and copy buttons next to each URL and command.
(() => {
  const menu = byId('menu'), scrim = byId('menu-scrim'), burger = byId('burger');
  const setMenu = (open: boolean) => { menu.hidden = scrim.hidden = !open; burger.setAttribute('aria-expanded', String(open)); };
  burger.addEventListener('click', () => setMenu(!!menu.hidden));
  scrim.addEventListener('click', () => setMenu(false));
  menu.addEventListener('click', (e) => { if ((e.target as Element).closest('a')) setMenu(false); });
  addEventListener('keydown', (e) => { if (e.key === 'Escape') setMenu(false); });

  const COPY = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a1 1 0 0 1 1-1h10"/></svg>';
  const DONE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7"/></svg>';
  for (const btn of all(document, '.copy')) {
    btn.innerHTML = COPY;
    btn.addEventListener('click', async () => {
      const text = btn.previousElementSibling?.textContent ?? '';
      try { await navigator.clipboard.writeText(text); }
      catch {
        const t = document.createElement('textarea');
        t.value = text; document.body.append(t); t.select();
        document.execCommand('copy'); t.remove();
      }
      btn.innerHTML = DONE;
      btn.classList.add('copied');
      setTimeout(() => { btn.innerHTML = COPY; btn.classList.remove('copied'); }, 1600);
    });
  }
})();
